import React, { useState } from 'react';
import { getDataUrl } from '../utils/apiConfig';
import './ReportSection.css';
import ReportSectionImgCard from './ReportSectionImgCard';
import ReportSectionTableRow from './ReportSectionTableRow';
import VisibilityCaret from './VisibilityCaret';
import PublicationAnalysisWorkshopSection from './PublicationAnalysisWorkshopSection';

const ReportSectionActivity1 = ({ selectedWorkshop, selectedWorkshopDetails }) => {

  let wsReports = selectedWorkshopDetails?.reports || {};
  const base = getDataUrl() + "/";

  let wsName = selectedWorkshop ? selectedWorkshop.name : "";

  const [isHidden, setIsHidden] = useState(true);
  const toggleVisibility = () => {
    setIsHidden(!isHidden);
  };

  return (
  <section >    
    <div className="hero is-primary is-bold reportSectionHeader">
      <div id="tagline" className="hero-body">
        <div className="container">
          <div className="columns">
            <div className="column is-12">
              <h1 className="title">
                Activity 1: Sol health challenges
                <VisibilityCaret onClick={toggleVisibility} isHidden={isHidden} />
              </h1>
            </div>
          </div>
          <div className="columns">
            {/* Left Column for Hero Text */}
            <div className="column is-half">              
              <h2 className="subtitle">
              In order to foster effective collaboration for healthy soils across Europe, it is needed to have a shared under-standing of the soil health and management challenges in each region. Therefore, the participants were asked to collect and share soil health and management challenges for the following domains: society, politics, environment, economics, technology, research, land management and other.
              </h2>
            </div>            
            {/* Right Column for Icons and Subtitles */}            
            <div className="column is-half is-flex justify-content-center align-items-center">
              <div className="columns is-vcentered is-mobile">
                {/* Icons and Text */}
                <div className="column has-text-centered">
                <span className="icon is-large"><img src={process.env.PUBLIC_URL + "/icons/puzzle.svg"} alt=""></img></span>
                  <p>Mapping of local soil health challenges</p>
                </div>                
                <div className="column has-text-centered">
                  <span className="icon is-large"><img src={process.env.PUBLIC_URL + "/icons/hand_seedling.svg"} alt=""></img></span>
                  <p>Mapping of soil management challenges</p>
                </div>                
                <div className="column has-text-centered">
                  <span className="icon is-large"><img src={process.env.PUBLIC_URL + "/icons/handshake.svg"} alt=""></img></span>
                  <p>Mapping of regional soil health objectives</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>    
    <div className={isHidden ? 'is-hidden' : 'container'}>
      <div className="columns">
        <div className="column is-6">          
          <ReportSectionImgCard
              title="Common-theme radar: soil health challenges"
              badgeText="Comparable across all workshops"
              imgSource={base + wsName + '/activity_1/radar_chart_soil_health_and_management.png'}
              imgAltText="Radar chart of soil health challenges identified in the workshop and ranked by AI."
              text={'This radar chart uses a shared set of broad soil health and management themes across all workshops. It is designed for comparing workshops and regions, regardless of land-use context.'} />
        </div>
        <div className="column is-6">          
          <ReportSectionImgCard
              title="Wordcloud"
              imgSource={base + wsName + '/activity_1/wordcloud.png'}
              imgAltText="Wordcloud from responses gathered during this activity of the workshop."
              text={'This wordcloud serves as a snapshot of the collective thoughts shared during this activity, providing a quick way to understand the main topics that emerged. It\'s a powerful tool for making sense of the collective knowledge and insights shared by the participants.'} />
        </div>
      </div>
      <div className="columns">
        <div className="column is-6">
          <ReportSectionImgCard
              title="Topic mapping"
              imgSource={base + wsName + '/activity_1/2D-predefined_categories.png'}
              imgAltText="2D projection of topic with predefined categories."
              text={'This 2D projection visualizes the clustering of workshop responses. Each colored dot represents a response, with colors indicating predefined categories. The visualization reveals natural clusters in the data, which do not perfectly align with the predefined categories, suggesting the need for a more data-driven approach to categorizing the responses.'}
              stackVertical={true} />
        </div>
        <div className="column is-6">          
          <ReportSectionImgCard
              title="Topic discovery"
              imgSource={base + wsName + '/activity_1/2D-discovered_topics.png'}
              imgAltText="2D projection of discovered topics."
              text={'This visualization presents the result of applying topic modeling to the workshop responses. The number of topics has changed, and each cluster now represents a distinct topic identified from the data. The topics, which are clear, compact, and focused, align more closely with the natural clusters observed in the data, providing a more accurate categorization of the responses.'} 
              stackVertical={true} />
        </div>
      </div>      
      <div className="publication-analysis-context-note">
        <span className="tag is-light">Two complementary views</span>
        <p>
          The common-theme radar above uses the same broad theme vocabulary for every workshop, making cross-region comparison easier. The land-use-specific radar below uses themes derived from workshops in the same land-use context, making it more precise for this workshop but less directly comparable across different land uses.
        </p>
      </div>
      <PublicationAnalysisWorkshopSection
        selectedWorkshop={selectedWorkshop}
        diagramKey="challenges"
      />
      <div className="columns text-by-image">
        <div className="column is-12">
          <div className="content">
            <h2>Challenges for soil health & soil management</h2>
            <p>The following table is summarizing the main challenges categorized in eight dimensions to uncover challenges for different aspects in our society. The results highlight the complexity of soil health and management issues, emphasizing the need for systemic approaches including all relevant stakeholders.</p>
          </div>
          <ReportSectionTableRow
            title="LAND MANAGEMENT"
            text={wsReports.text_activity_1_soil_challenges_land_mgmt}
            isEven={true}
          />
          <ReportSectionTableRow
            title="SOCIETAL"
            text={wsReports.text_activity_1_soil_challenges_social}
          />          
          <ReportSectionTableRow
            title="ENVIRONMENTAL"
            text={wsReports.text_activity_1_soil_challenges_environment}
            isEven={true}
          />
          <ReportSectionTableRow
            title="POLITICAL"
            text={wsReports.text_activity_1_soil_challenges_political}            
          />
          <ReportSectionTableRow
            title="ECONOMICAL"
            text={wsReports.text_activity_1_soil_challenges_economical}
            isEven={true}
          />
          <ReportSectionTableRow
            title="TECHNOLOGICAL"
            text={wsReports.text_activity_1_soil_challenges_technological}
          />
          <ReportSectionTableRow
            title="RESEARCH"
            text={wsReports.text_activity_1_soil_challenges_research}
            isEven={true}
          />
        </div>        
      </div>      
    </div>
  </section>
  );
};

export default ReportSectionActivity1;
